import {
  alignChannelFromBackend,
  type Channel,
  isExportChannel,
  isExternalChannel,
  isIntegrationChannel,
} from 'src/data/channel';
import { parseJson } from 'src/lib/common';

import { LOADING_TALKING_POINTS_TEXT } from './const';
import { PERFORMANCE_EXTRACTION_STATUS, type PerformanceExtractionStatus } from './extraction';
import { isEmailCsvResourceDocument } from './resources';
import type { IntegrationAccount, IntegrationAsset, IntegrationAssetResult } from './types';

export const flatConnectedAccounts = (
  connectedAccounts: Record<Channel, IntegrationAsset[]>,
  withResources: boolean
): IntegrationAsset[] => {
  if (withResources) {
    return Object.values(connectedAccounts || {}).reduce((acc: IntegrationAsset[], accounts) => {
      return acc.concat(accounts);
    }, []);
  }

  return Object.keys(connectedAccounts || {}).reduce((acc: IntegrationAsset[], channel) => {
    if (isIntegrationChannel(channel) || isExternalChannel(channel) || isExportChannel(channel)) {
      return acc.concat(connectedAccounts[channel]);
    }
    return acc;
  }, []);
};

export const prepareAccount = (
  account: IntegrationAsset,
  channel: Channel
): IntegrationAccount => ({
  ...account,
  name: account.accountName,
  id: account.accountId,
  integrationAssetId: account.id,
  channel,
});

export const convertAccountsListToChannelMap = (
  accounts: IntegrationAsset[] = []
): Record<Channel, IntegrationAccount[]> => {
  const result: Record<Channel, IntegrationAccount[]> = {};

  accounts.forEach((account) => {
    const channel = alignChannelFromBackend(account.dataSource);
    const channelAccounts = result[channel] || [];
    const preparedAccount = prepareAccount(account, channel);

    result[channel] = [...channelAccounts, preparedAccount];
  });

  return result;
};

export const getPerformanceLoaderLabel = ({
  channel,
  progressPercent,
  isTrainingModel,
  isAlreadyConnected,
}: {
  channel: Channel;
  progressPercent: number;
  isTrainingModel?: boolean;
  isAlreadyConnected?: boolean;
}) => {
  if (isTrainingModel && isAlreadyConnected) {
    return 'Training model...';
  }

  if (progressPercent < 25) {
    return isEmailCsvResourceDocument(channel) ? 'Uploading...' : `Downloading data...`;
  }
  if (progressPercent < 50) {
    return 'Defining default performance benchmark sets...';
  }
  if (progressPercent < 75) {
    return LOADING_TALKING_POINTS_TEXT;
  }
  return 'Preparing data for custom models...';
};

export const isAccountConnected = (account: IntegrationAsset): boolean =>
  (
    [
      PERFORMANCE_EXTRACTION_STATUS.connected,
      PERFORMANCE_EXTRACTION_STATUS.done,
    ] as PerformanceExtractionStatus[]
  ).includes(account.status);

export const isAccountInProgress = (account: IntegrationAsset): boolean =>
  (
    [
      PERFORMANCE_EXTRACTION_STATUS.processing,
      PERFORMANCE_EXTRACTION_STATUS.refreshing,
    ] as PerformanceExtractionStatus[]
  ).includes(account.status);

/**
 * Map BE PerformanceCenterTextVariationsResult record to FE consumed format
 * @param item - BE data
 * @returns - FE data
 */
// TODO: improve return type
export const mapBackendPerformanceTextVariation = (
  item: IntegrationAssetResult
): Record<string, unknown> => {
  const parsedPerformance = parseJson(item.performance, {});
  return {
    ...item,
    weightedCtr: item.weightedAvgCtr,
    impressions: item.lifetimeImpressions,
    performance: parsedPerformance,
    ...parsedPerformance,
  };
};
